import { toggleMark } from 'prosemirror-commands';
import { Plugin, PluginKey } from 'prosemirror-state';
import { Decoration, DecorationSet, EditorView } from 'prosemirror-view';
import { isUrl } from '../utils/url';
import { Extension } from './base/Extension';

export class PasteHandler extends Extension {
  constructor() {
    super('paste-handler');
  }

  private key = new PluginKey(this.name);

  private insertLink(view: EditorView, href: string, title?: string) {
    const { state } = view;
    const { from } = state.selection;
    const to = from + (title ?? href).length;

    const transaction = state.tr
      .insertText(title ?? href, state.selection.from, state.selection.to)
      .addMark(from, to, state.schema.marks.link.create({ href }))
      .setMeta(this.key, { add: { from, to, id: href } });
    view.dispatch(transaction);
  }

  plugins(): Plugin[] {
    return [
      new Plugin({
        key: this.key,
        props: {
          transformPastedHTML(html: string) {
            if (isDropboxPaper(html)) {
              // Fixes double paragraphs when pasting from Dropbox Paper
              html = html.replace(/<div><br><\/div>/gi, '<p></p>');
            }
            return html;
          },
          handleDOMEvents: {
            keydown: (_, event) => {
              if (event.key === 'Shift') {
                this.shiftKey = true;
              }
              return false;
            },
            keyup: (_, event) => {
              if (event.key === 'Shift') {
                this.shiftKey = false;
              }
              return false;
            },
            cut: (view) => {
              console.log('view', view.state.doc.toJSON());
              return false;
            },
          },
          handlePaste: (view, event: ClipboardEvent) => {
            // Do nothing if the document isn't currently editable or there is no clipboard data
            if (!view.editable || !event.clipboardData) {
              return false;
            }

            const { state, dispatch } = view;
            const iframeSrc = parseSingleIframeSrc(event.clipboardData.getData('text/plain'));
            const text = iframeSrc ? iframeSrc : event.clipboardData.getData('text/plain');
            const html = event.clipboardData.getData('text/html');

            if (!this.shiftKey) {
              // If the HTML on the clipboard is from Prosemirror then the best
              // compatibility is to just use the HTML parser, regardless of
              // whether it "looks" like Markdown, see: outline/outline#2416
              if (html?.includes('data-pm-slice')) {
                return false;
              }

              // Check if the clipboard contents can be parsed as a single url
              if (isUrl(text)) {
                // If there is selected text then we want to wrap it in a link to the url
                if (!state.selection.empty) {
                  toggleMark(state.schema.marks.link, { href: text })(state, dispatch);
                  return true;
                } else {
                  this.insertLink(view, text);
                }

                return true;
              }
            }

            // otherwise use the default HTML parser which will handle all paste
            // "from the web" events
            return false;
          },
        },
        state: {
          init: () => DecorationSet.empty,
          apply: (tr, set) => {
            const mapping = tr.mapping;

            // See if the transaction adds or removes any placeholders
            const meta = tr.getMeta(this.key);

            // We only want a single paste placeholder at a time, so if we're adding a new
            // placeholder we can just return a new DecorationSet and avoid mapping logic.
            if (meta?.add) {
              const { from, to, id } = meta.add;
              const decorations = [
                Decoration.inline(
                  from,
                  to,
                  {
                    class: 'paste-placeholder',
                  },
                  {
                    id,
                  }
                ),
              ];
              return DecorationSet.create(tr.doc, decorations);
            }

            set = set.map(mapping, tr.doc);

            if (meta?.remove) {
              const { id } = meta.remove;
              const decorations = set.find(undefined, undefined, (spec) => spec.id === id);
              return set.remove(decorations);
            }

            return set;
          },
        },
      }),
    ];
  }

  private shiftKey = false;
}

/**
 * Checks if the HTML string is likely coming from Dropbox Paper.
 *
 * @param html The HTML string to check.
 * @returns True if the HTML string is likely coming from Dropbox Paper.
 */
function isDropboxPaper(html: string): boolean {
  return html?.includes('usually-unique-id');
}

/**
 * Parses the text contents of an HTML string and returns the src of the first
 * iframe if it exists.
 *
 * @param text The HTML string to parse.
 * @returns The src of the first iframe if it exists, or undefined.
 */
function parseSingleIframeSrc(html: string) {
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    if (doc.body.children.length === 1 && doc.body.firstElementChild?.tagName === 'IFRAME') {
      const iframe = doc.body.firstElementChild;
      const src = iframe.getAttribute('src');
      if (src) {
        return src;
      }
    }
  } catch {
    // Ignore the million ways parsing could fail.
  }
  return undefined;
}
